import React from 'react'
import { Button } from 'antd'
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary'
import type { ErrorInfo, ReactNode } from 'react'
import type { FallbackProps } from 'react-error-boundary'
import { streamApplicationError } from '../../../_utils/airbrake'
import { useAccessOwner } from '../../access/access-owner-provider'
import type { AppAccessOwner } from '../../access/access-owner-provider'
import { APPLICATION_VERSION } from '../../../constants'

export type ErrorBoundaryLayer = 'root' | 'boot' | 'route' | 'widget'

export type AppErrorBoundaryProps = {
    layer: ErrorBoundaryLayer
    /** When any entry changes, a boundary that has caught an error clears itself. */
    resetKeys?: unknown[]
    /** Replaces the layer's built-in fallback. */
    fallback?: ReactNode
    children?: ReactNode
}

type LayerCopy = {
    title: string
    detail: string
    action: string
}

const COPY: Record<ErrorBoundaryLayer, LayerCopy> = {
    root: {
        title: 'Something went wrong',
        detail: 'The page could not be displayed. Reloading usually fixes it.',
        action: 'Reload',
    },
    boot: {
        title: 'We could not finish loading the app',
        detail: 'Something interrupted startup. Try again in a moment.',
        action: 'Retry',
    },
    route: {
        title: 'This page could not be displayed',
        detail: 'You can keep using the menu to go somewhere else, or try this page again.',
        action: 'Retry',
    },
    widget: {
        title: 'This section could not be displayed.',
        detail: '',
        action: 'Retry',
    },
}

const LAYOUT: Record<ErrorBoundaryLayer, React.CSSProperties> = {
    root: { textAlign: 'center', paddingTop: '20vh' },
    boot: { textAlign: 'center', paddingTop: '20vh' },
    route: { textAlign: 'center', padding: '10vh 24px' },
    widget: { display: 'flex', alignItems: 'center', gap: 8, padding: 8 },
}

const reloadPage = () => window.location.reload()

const LayerFallback = ({ layer, resetErrorBoundary }: { layer: ErrorBoundaryLayer } & FallbackProps) => {
    const copy = COPY[layer]
    const onAction = layer === 'root' ? reloadPage : () => resetErrorBoundary()
    const isInline = layer === 'widget'

    return (
        <div role="alert" data-testid={`error-boundary-${layer}`} style={LAYOUT[layer]}>
            {isInline ? (
                <span>{copy.title}</span>
            ) : (
                <>
                    <div style={{ fontSize: 24, fontWeight: 'bold' }}>{copy.title}</div>
                    <div style={{ fontSize: 16, margin: '8px 0 16px' }}>{copy.detail}</div>
                </>
            )}
            <Button type="primary" size={isInline ? 'small' : 'large'} onClick={onAction}>
                {copy.action}
            </Button>
        </div>
    )
}

/** The open domain or organization as ids only, so a report can be tied to a tenant without carrying anything else. */
const openTenant = (owner: AppAccessOwner): { domainId: number } | { orgId: number } | undefined => {
    const context = owner.context
    if (!context) {
        return undefined
    }
    return context.kind === 'domain' ? { domainId: context.domainId } : { orgId: context.orgId }
}

/**
 * Reporting happens here and nowhere else: a fallback can render more than once for one
 * failure, which would report the same error repeatedly. The context is what a report needs to be
 * found and placed: the layer, the route path (never its query), the release and the tenant.
 */
const reportBoundaryError = (owner: AppAccessOwner, layer: ErrorBoundaryLayer, error: unknown, info: ErrorInfo) => {
    streamApplicationError(error, {
        layer,
        componentStack: info.componentStack,
        context: { route: window.location.pathname, release: APPLICATION_VERSION, tenant: openTenant(owner) },
    })
}

export const AppErrorBoundary = ({ layer, resetKeys, fallback, children }: AppErrorBoundaryProps) => {
    const owner = useAccessOwner()
    const onError = (error: unknown, info: ErrorInfo) => reportBoundaryError(owner, layer, error, info)

    if (fallback !== undefined) {
        return (
            <ErrorBoundary fallback={fallback} onError={onError} resetKeys={resetKeys}>
                {children}
            </ErrorBoundary>
        )
    }

    return (
        <ErrorBoundary
            fallbackRender={(props) => <LayerFallback layer={layer} {...props} />}
            onError={onError}
            resetKeys={resetKeys}
        >
            {children}
        </ErrorBoundary>
    )
}

/** Returns a function that sends an error from a handler or async callback to the nearest boundary. */
export const useBoundaryError = (): ((error: unknown) => void) => useErrorBoundary().showBoundary
